import { create } from 'zustand';

// Toasts. 06-qol §4: bottom-right, auto-dismiss after 5s (10s when they carry an
// action), stacking rather than replacing, and dismissible.
//
// The undo toast is the important one. CLAUDE.md §5a rule 8: undo beats confirm —
// a reversible action just happens, and the user gets ten seconds to take it back.
// Confirmation dialogs are reserved for the genuinely irreversible.

export type ToastTone = 'success' | 'warning' | 'danger' | 'info';

const DISMISS_MS = 5_000;
const DISMISS_WITH_ACTION_MS = 10_000;

export interface Toast {
  readonly id: string;
  readonly tone: ToastTone;
  /** Sentence case, no terminal punctuation, and never the word "successfully"
   *  (06-qol §4 and §11): "Candidate advanced", not "Candidate advanced successfully". */
  readonly message: string;
  /** What happened and what to do next. Never a raw technical error. */
  readonly detail?: string;
  readonly action?: {
    readonly label: string;
    run(): void;
  };
  /** Milliseconds the toast stays up, for the draining timer bar. */
  readonly durationMs: number;
  /**
   * Toasts sharing a key replace each other instead of stacking, and the
   * replacement restarts the timer. For feedback on a control the user may click
   * repeatedly, where a stack of near-identical toasts is noise, not information.
   */
  readonly key?: string;
}

let nextId = 0;

interface ToastState {
  readonly toasts: readonly Toast[];
  show(toast: Omit<Toast, 'id' | 'durationMs'>): string;
  /** The common case, so callers do not have to remember the ten-second window. */
  showUndo(message: string, undo: () => void): string;
  dismiss(id: string): void;
  clear(): void;
}

export const useToasts = create<ToastState>((set) => ({
  toasts: [],

  show: (toast) => {
    nextId += 1;
    const id = `toast-${nextId}`;
    const durationMs = toast.action ? DISMISS_WITH_ACTION_MS : DISMISS_MS;
    // A fresh id, even when replacing, is what restarts the timer and its bar.
    set((state) => ({
      toasts: [
        ...state.toasts.filter((t) => toast.key === undefined || t.key !== toast.key),
        { ...toast, id, durationMs },
      ],
    }));
    return id;
  },

  showUndo: (message, undo) => {
    nextId += 1;
    const id = `toast-${nextId}`;
    set((state) => ({
      toasts: [
        ...state.toasts,
        {
          id,
          tone: 'info',
          message,
          durationMs: DISMISS_WITH_ACTION_MS,
          action: {
            label: 'Undo',
            run: () => {
              undo();
              // The window closes the moment it is used — leaving the toast up
              // invites a second click that would undo the undo.
              set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
            },
          },
        },
      ],
    }));
    return id;
  },

  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));
